import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getUserOrders, Order } from "../api/order";
import { ShoppingBag } from "lucide-react";
import { PAGE_SIZE } from "../utils/utils";

interface OrderHistoryProps {
  onViewOrder: (order: Order) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "DELIVERED":
      return "bg-green-100 text-green-800";
    case "CANCELLED":
      return "bg-red-100 text-red-800";
    case "IN_DELIVERY":
      return "bg-blue-100 text-blue-800";
    case "PENDING":
    case "VALIDATED":
      return "bg-yellow-100 text-yellow-800";
    case "CREATED":
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const OrderHistory: React.FC<OrderHistoryProps> = ({ onViewOrder }) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      const ordersResponse = await getUserOrders(currentPage, PAGE_SIZE); // fetch 10 recent orders
      if (ordersResponse.error) {
        console.error("Failed to fetch orders:", ordersResponse.error);
        setError(ordersResponse.error.message);
      } else {
        setOrders(ordersResponse.data?.content || []);
        setTotalPages(ordersResponse.data?.totalPages || 0);
      }
      setIsLoading(false);
    };

    fetchOrders();
  }, [currentPage]);

  if (isLoading) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-16 bg-gray-200 rounded-lg mb-4"></div>
        <div className="h-16 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500">
        {t("profile.noOrders", "Could not load orders.")}
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-xl p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <ShoppingBag className="w-5 h-5 mr-2 text-orange-500" />
        {t("profile.orderHistory", "Order History")}
      </h3>
      {orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              onClick={() => onViewOrder(order)}
              className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-gray-800">
                    Order #{order.id.substring(0, 8)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-800">
                    {order.totalPrice.toLocaleString(undefined, {
                      style: "currency",
                      currency: "CAD",
                    })}
                  </p>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {order.status === "DELIVERED" && order.type === "PICKUP"
                      ? t(`orderStatus.ready`)
                      : t(
                          `orderStatus.${order.status.toLowerCase()}`,
                          order.status
                        )}
                  </span>
                </div>
              </div>

              {/* Order items summary */}
              {order.items.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center">
                    <div className="flex -space-x-2 overflow-hidden mr-4">
                      {order.items.slice(0, 3).map((item) => (
                        <img
                          key={item.dish.id}
                          className="inline-block h-10 w-10 rounded-full ring-2 ring-white"
                          src={item.dish.imageUrl}
                          alt={item.dish.name}
                        />
                      ))}
                      {order.items.length > 3 && (
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-200 ring-2 ring-white">
                          <span className="text-xs font-semibold text-gray-600">
                            +{order.items.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {order.items
                        .slice(0, 2)
                        .map((item) => item.dish.name)
                        .join(", ")}
                      {order.items.length > 2 ? " and more..." : ""}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* ➡️ Composant de Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6">
              {/* Bouton Précédent */}
              <button
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border transition-colors duration-200
        ${
          currentPage === 0
            ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
            : "bg-white text-orange-600 border-gray-300 hover:bg-gray-50"
        }`}
              >
                {t("pagination.previous")}
              </button>

              {/* Affichage des numéros de page */}
              {Array.from({ length: totalPages }, (_, i) => i)
                .slice(
                  Math.max(0, currentPage - 2),
                  Math.min(totalPages, currentPage + 3)
                ) // Afficher 5 pages max autour de la page actuelle
                .map((pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-4 py-2 text-sm font-medium border-t border-b transition-colors duration-200
            ${
              pageNumber === currentPage
                ? "bg-orange-600 text-white border-blue-600 z-10"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }
          `}
                  >
                    {pageNumber + 1}
                  </button>
                ))}

              {/* Bouton Suivant */}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                }
                disabled={currentPage === totalPages - 1}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border transition-colors duration-200
        ${
          currentPage === totalPages - 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
            : "bg-white text-orange-600 border-gray-300 hover:bg-gray-50"
        }
      `}
              >
                {t("pagination.next")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-500">
          {t("profile.noOrders", "You have no orders yet.")}
        </p>
      )}
    </div>
  );
};

export default OrderHistory;
